import { doc, getDoc, setDoc, updateDoc, serverTimestamp, getFirestore } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface UserProgress {
  // Profile completion
  profileCompleted: boolean;
  tradingExperienceCompleted: boolean;
  preferredPairsCompleted: boolean;
  xmAccountVerified: boolean;
  
  // XM Level System
  currentXmLevel: 'none' | 'bronze' | 'silver' | 'gold';
  xmPoints: number;
  xmLevelValidUntil: Date | null;
  
  // Monthly Points System
  currentMonth?: string | null; // Format: "2024-01" for January 2024
  monthlyCommission?: number | null; // Commission earned this month
  monthlyPoints?: number | null; // Points earned this month (commission * 10)
  totalCommission?: number | null; // All-time total commission
  commissionPoints?: number | null; // All-time total points
  
  // Level Maintenance
  levelMaintenanceStatus?: 'safe' | 'warning' | 'risk' | null; // Based on 75% threshold
  lastMonthlyReset?: Date | null;
  
  // Achievement timestamps
  bronzeAchievedAt?: Date | null;
  silverAchievedAt?: Date | null;
  goldAchievedAt?: Date | null;
  
  // Last calculation
  lastCalculated: Date;
}

export interface XmLevelRequirements {
  bronze: {
    profileCompleted: boolean;
    tradingExperienceCompleted: boolean;
    preferredPairsCompleted: boolean;
    xmAccountVerified: boolean;
  };
  silver: {
    // Will be implemented later
    bronzeCompleted: boolean;
    minimumTrades?: number;
    minimumVolume?: number;
  };
  gold: {
    // Will be implemented later
    silverCompleted: boolean;
    minimumTrades?: number;
    minimumVolume?: number;
  };
}

class PointsService {
  private static instance: PointsService;

  public static getInstance(): PointsService {
    if (!PointsService.instance) {
      PointsService.instance = new PointsService();
    }
    return PointsService.instance;
  }

  private constructor() {}

  /**
   * Get current month string in format "YYYY-MM"
   */
  private getCurrentMonth(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  /**
   * Get start and end dates for current month
   */
  private getCurrentMonthDates(): { start: Date; end: Date } {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
  }

  /**
   * Check if monthly reset is needed
   */
  private needsMonthlyReset(userProgress: UserProgress | null): boolean {
    if (!userProgress) return false;
    const currentMonth = this.getCurrentMonth();
    return userProgress.currentMonth !== currentMonth;
  }

  /**
   * Calculate level maintenance status based on 75% threshold
   */
  private calculateMaintenanceStatus(currentLevel: string, monthlyPoints: number): 'safe' | 'warning' | 'risk' {
    if (currentLevel === 'bronze' || currentLevel === 'none') {
      return 'safe'; // Bronze doesn't require point maintenance
    }
    
    const target = currentLevel === 'silver' ? 500 : 1000; // Silver: 500, Gold: 1000
    const threshold = target * 0.75; // 75% threshold
    
    if (monthlyPoints >= threshold) {
      return 'safe';
    } else if (monthlyPoints >= threshold * 0.5) { // 37.5% - halfway to danger
      return 'warning';
    } else {
      return 'risk';
    }
  }

  /**
   * Fetch trader statistics from traderStatistics collection by XM account number
   */
  private async getTraderStatistics(userProfile: any): Promise<{ totalCommission: number; monthlyCommission: number } | null> {
    try {
      // Get the user's XM account number
      const xmAccountNumber = userProfile?.xmAccountNumber;
      if (!xmAccountNumber) {
        return { totalCommission: 0, monthlyCommission: 0 };
      }

      // Query traderStatistics collection for trades by this user
      const { collection, query, where, getDocs, Timestamp } = await import('firebase/firestore');
      
      // Ensure account number is a number for the query
      const accountAsNumber = typeof xmAccountNumber === 'string' ? parseInt(xmAccountNumber) : xmAccountNumber;
      
      const tradesQuery = query(
        collection(db, 'traderStatistics'),
        where('loginId', '==', accountAsNumber)
      );
      
      const querySnapshot = await getDocs(tradesQuery);
      
      let totalCommission = 0;
      let monthlyCommission = 0;
      let validTrades = 0;
      
      // Get current month date range
      const { start: monthStart, end: monthEnd } = this.getCurrentMonthDates();
      
      querySnapshot.forEach((doc) => {
        const trade = doc.data();
        const commissionValue = trade.commission || 0;
        
        if (commissionValue !== 0) {
          const absCommission = Math.abs(commissionValue);
          // Add to total commission
          totalCommission += absCommission;
          validTrades++;
          
          // Check if trade is from current month - handle both Firebase Timestamps and string dates
          let tradeDate: Date | null = null;
          if (trade.closeTime) {
            if (typeof trade.closeTime === 'string') {
              tradeDate = new Date(trade.closeTime);
            } else if (trade.closeTime.toDate) {
              tradeDate = trade.closeTime.toDate();
            }
          } else if (trade.openTime) {
            if (typeof trade.openTime === 'string') {
              tradeDate = new Date(trade.openTime);
            } else if (trade.openTime.toDate) {
              tradeDate = trade.openTime.toDate();
            }
          }
                    const isInCurrentMonth = tradeDate && tradeDate >= monthStart && tradeDate <= monthEnd;
          
          if (isInCurrentMonth) {
            monthlyCommission += absCommission;
          }
        }
      });
      
      return { totalCommission, monthlyCommission };
    } catch (error) {
      console.error('Error fetching trader statistics:', error);
      return { totalCommission: 0, monthlyCommission: 0 };
    }
  }

  /**
   * Calculate commission points: $1 commission = 10 points
   */
  private calculateCommissionPoints(totalCommission: number): number {
    return Math.floor(totalCommission * 10);
  }

  /**
   * Check if user meets Bronze level requirements
   */
  public checkBronzeRequirements(userProfile: any): boolean {
    const tradingExperienceCompleted = !!(userProfile.tradingExperience && userProfile.tradingExperience.trim());
    const preferredPairsCompleted = !!(userProfile.preferredTradingPairs && userProfile.preferredTradingPairs.trim());
    const xmAccountVerified = !!userProfile.xmAffiliationVerified;
    const hasXmAccountNumber = !!(userProfile.xmAccountNumber && userProfile.xmAccountNumber.trim());
    
    // Require both verification status AND actual account number
    const xmRequirementsMet = xmAccountVerified && hasXmAccountNumber;
    
    return tradingExperienceCompleted && preferredPairsCompleted && xmRequirementsMet;
  }

  /**
   * Check if user meets Silver level requirements
   */
  public async checkSilverRequirements(userId: string, userProfile: any): Promise<{ 
    bronzeCompleted: boolean; 
    commissionPointsEarned: boolean; 
    totalCommission: number; 
    commissionPoints: number;
    monthlyCommission: number;
    monthlyPoints: number;
  }> {
    const bronzeCompleted = this.checkBronzeRequirements(userProfile);
    const traderStats = await this.getTraderStatistics(userProfile);
    
    const totalCommission = traderStats?.totalCommission || 0;
    const monthlyCommission = traderStats?.monthlyCommission || 0;
    const monthlyPoints = this.calculateCommissionPoints(monthlyCommission);
    const commissionPointsEarned = monthlyPoints >= 500; // Need 500 monthly points for Silver
    
    return {
      bronzeCompleted,
      commissionPointsEarned,
      totalCommission,
      commissionPoints: this.calculateCommissionPoints(totalCommission), // All-time points for compatibility
      monthlyCommission,
      monthlyPoints
    };
  }

  /**
   * Get detailed progress for Bronze level
   */
  public getBronzeProgress(userProfile: any): {
    completed: boolean;
    requirements: {
      tradingExperience: { completed: boolean; label: string; };
      preferredPairs: { completed: boolean; label: string; };
      xmAccountVerified: { completed: boolean; label: string; };
    };
    completionPercentage: number;
  } {
    const tradingExperienceCompleted = !!(userProfile.tradingExperience && userProfile.tradingExperience.trim());
    const preferredPairsCompleted = !!(userProfile.preferredTradingPairs && userProfile.preferredTradingPairs.trim());
    const xmAccountVerified = !!userProfile.xmAffiliationVerified;
    const hasXmAccountNumber = !!(userProfile.xmAccountNumber && userProfile.xmAccountNumber.trim());
    
    // Require both verification status AND actual account number
    const xmRequirementsMet = xmAccountVerified && hasXmAccountNumber;

    const requirements = {
      tradingExperience: {
        completed: tradingExperienceCompleted,
        label: 'Set Trading Experience Level'
      },
      preferredPairs: {
        completed: preferredPairsCompleted,
        label: 'Add Preferred Trading Pairs'
      },
      xmAccountVerified: {
        completed: xmRequirementsMet,
        label: 'Verify XM Account Affiliation'
      }
    };

    const completedCount = Object.values(requirements).filter(req => req.completed).length;
    const totalCount = Object.keys(requirements).length;
    const completionPercentage = Math.round((completedCount / totalCount) * 100);

    return {
      completed: completedCount === totalCount,
      requirements,
      completionPercentage
    };
  }

  /**
   * Check if user meets Gold level requirements
   */
  public async checkGoldRequirements(userId: string, userProfile: any): Promise<{ 
    silverCompleted: boolean; 
    commissionPointsEarned: boolean; 
    totalCommission: number; 
    commissionPoints: number;
    monthlyCommission: number;
    monthlyPoints: number;
  }> {
    const silverReqs = await this.checkSilverRequirements(userId, userProfile);
    
    const monthlyPoints = silverReqs.monthlyPoints;
    const commissionPointsEarned = monthlyPoints >= 1000; // Need 1000 monthly points for Gold
    
    return {
      silverCompleted: silverReqs.bronzeCompleted && silverReqs.commissionPointsEarned,
      commissionPointsEarned,
      totalCommission: silverReqs.totalCommission,
      commissionPoints: silverReqs.commissionPoints,
      monthlyCommission: silverReqs.monthlyCommission,
      monthlyPoints
    };
  }

  /**
   * Get detailed progress for Silver level
   */
  public async getSilverProgress(userId: string, userProfile: any): Promise<{
    completed: boolean;
    requirements: {
      bronzeCompleted: { completed: boolean; label: string; };
      commissionPoints: { completed: boolean; label: string; progress: string; };
    };
    completionPercentage: number;
    totalCommission: number;
    commissionPoints: number;
  }> {
    const silverReqs = await this.checkSilverRequirements(userId, userProfile);

    const requirements = {
      bronzeCompleted: {
        completed: silverReqs.bronzeCompleted,
        label: 'Complete Bronze Level'
      },
              commissionPoints: {
          completed: silverReqs.commissionPointsEarned,
          label: 'Earn 500 Points',
          progress: `${silverReqs.commissionPoints}/500 points`
        }
    };

    // Calculate completion percentage based on points progress toward 500
    const pointsProgress = Math.min(silverReqs.commissionPoints / 500, 1.0); // Cap at 100%
    const bronzeProgress = silverReqs.bronzeCompleted ? 1.0 : 0.0;
    
    // Weight: 20% for Bronze completion, 80% for points progress toward 500
    const completionPercentage = Math.round((bronzeProgress * 0.2 + pointsProgress * 0.8) * 100);
    
    const completedCount = Object.values(requirements).filter(req => req.completed).length;
    const totalCount = Object.keys(requirements).length;

    return {
      completed: completedCount === totalCount,
      requirements,
      completionPercentage,
      totalCommission: silverReqs.totalCommission,
      commissionPoints: silverReqs.commissionPoints
    };
  }

  /**
   * Get detailed progress for Gold level
   */
  public async getGoldProgress(userId: string, userProfile: any): Promise<{
    completed: boolean;
    requirements: {
      silverCompleted: { completed: boolean; label: string; };
      commissionPoints: { completed: boolean; label: string; progress: string; };
    };
    completionPercentage: number;
    totalCommission: number;
    commissionPoints: number;
  }> {
    const goldReqs = await this.checkGoldRequirements(userId, userProfile);

    const requirements = {
      silverCompleted: {
        completed: goldReqs.silverCompleted,
        label: 'Complete Silver Level'
      },
      commissionPoints: {
        completed: goldReqs.commissionPointsEarned,
        label: 'Earn 1000 Points',
        progress: `${goldReqs.commissionPoints}/1000 points`
      }
    };

    // Calculate completion percentage based on points progress toward 1000
    const pointsProgress = Math.min(goldReqs.commissionPoints / 1000, 1.0); // Cap at 100%
    const silverProgress = goldReqs.silverCompleted ? 1.0 : 0.0;
    
    // Weight: 20% for Silver completion, 80% for points progress toward 1000
    const completionPercentage = Math.round((silverProgress * 0.2 + pointsProgress * 0.8) * 100);
    
    const completedCount = Object.values(requirements).filter(req => req.completed).length;
    const totalCount = Object.keys(requirements).length;

    return {
      completed: completedCount === totalCount,
      requirements,
      completionPercentage,
      totalCommission: goldReqs.totalCommission,
      commissionPoints: goldReqs.commissionPoints
    };
  }

  /**
   * Calculate and update user's XM level
   */
  public async calculateXmLevel(userId: string, userProfile: any): Promise<UserProgress> {
    const existingProgress = await this.getUserProgress(userId);
    const currentMonth = this.getCurrentMonth();
    
    // Check if we need monthly reset
    const needsReset = this.needsMonthlyReset(existingProgress);
    
    const bronzeCompleted = this.checkBronzeRequirements(userProfile);
    const silverReqs = await this.checkSilverRequirements(userId, userProfile);
    const goldReqs = await this.checkGoldRequirements(userId, userProfile);
    
    // Handle monthly level downgrade if needed
    let currentLevel: 'none' | 'bronze' | 'silver' | 'gold' = 'none';
    
    // Check if user's current level is still valid based on requirements
    // If user removes XM account, they should immediately lose Bronze+ levels
    const hasValidBronzeRequirements = bronzeCompleted;
    const existingLevel = existingProgress?.currentXmLevel || 'none';
    
    // If user previously had Bronze+ level but no longer meets Bronze requirements,
    // immediately downgrade to 'none' regardless of other factors
    if (existingLevel !== 'none' && !hasValidBronzeRequirements) {
      currentLevel = 'none';
    } else if (needsReset && existingProgress) {
      // Check if user maintained their level from last month
      const lastMonthPoints = existingProgress.monthlyPoints || 0;
      const lastLevel = existingProgress.currentXmLevel;
      
      if (lastLevel === 'gold' && lastMonthPoints < 750) { // 75% of 1000
        // Downgrade from Gold, but check if they still meet Bronze requirements
        if (bronzeCompleted) {
          currentLevel = 'silver'; // Downgrade to Silver
        } else {
          currentLevel = 'none'; // Fall back to none if Bronze requirements not met
        }
      } else if (lastLevel === 'silver' && lastMonthPoints < 375) { // 75% of 500
        // Downgrade from Silver, but check if they still meet Bronze requirements
        if (bronzeCompleted) {
          currentLevel = 'bronze'; // Downgrade to Bronze
        } else {
          currentLevel = 'none'; // Fall back to none if Bronze requirements not met
        }
      } else {
        // No downgrade, check for upgrades based on current month
        if (goldReqs.silverCompleted && goldReqs.commissionPointsEarned) {
          currentLevel = 'gold';
        } else if (silverReqs.bronzeCompleted && silverReqs.commissionPointsEarned) {
          currentLevel = 'silver';
        } else if (bronzeCompleted) {
          currentLevel = 'bronze';
        }
      }
    } else {
      // Either no reset needed, or user still meets requirements - proceed with normal calculation
      if (goldReqs.silverCompleted && goldReqs.commissionPointsEarned) {
        currentLevel = 'gold';
      } else if (silverReqs.bronzeCompleted && silverReqs.commissionPointsEarned) {
        currentLevel = 'silver';
      } else if (bronzeCompleted) {
        currentLevel = 'bronze';
      }
    }
    
    // Calculate XM points
    const monthlyPoints = silverReqs.monthlyPoints;
    const xmPoints = bronzeCompleted ? 100 + monthlyPoints : monthlyPoints;
    
    // Calculate maintenance status
    const maintenanceStatus = this.calculateMaintenanceStatus(currentLevel, monthlyPoints);
    
    // Level valid until end of current month
    const now = new Date();
    const validUntil = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of current month

    const progress: UserProgress = {
      profileCompleted: bronzeCompleted,
      tradingExperienceCompleted: !!(userProfile.tradingExperience && userProfile.tradingExperience.trim()),
      preferredPairsCompleted: !!(userProfile.preferredTradingPairs && userProfile.preferredTradingPairs.trim()),
      xmAccountVerified: !!userProfile.xmAffiliationVerified,
      
      currentXmLevel: currentLevel,
      xmPoints,
      xmLevelValidUntil: validUntil,
      
      // Monthly points system
      currentMonth,
      monthlyCommission: silverReqs.monthlyCommission,
      monthlyPoints,
      totalCommission: silverReqs.totalCommission,
      commissionPoints: silverReqs.commissionPoints,
      
      // Level maintenance
      levelMaintenanceStatus: maintenanceStatus,
      lastMonthlyReset: needsReset ? now : (existingProgress?.lastMonthlyReset || null),
      
      lastCalculated: now
    };

    // Add achievement timestamps
    if (bronzeCompleted && currentLevel === 'bronze' && !existingProgress?.bronzeAchievedAt) {
      progress.bronzeAchievedAt = now;
    }
    
    if (currentLevel === 'silver' && !existingProgress?.silverAchievedAt) {
      progress.silverAchievedAt = now;
      // Keep Bronze achievement timestamp if it exists
      if (existingProgress?.bronzeAchievedAt) {
        progress.bronzeAchievedAt = existingProgress.bronzeAchievedAt;
      }
    }
    
    if (currentLevel === 'gold' && !existingProgress?.goldAchievedAt) {
      progress.goldAchievedAt = now;
      // Keep previous achievement timestamps if they exist
      if (existingProgress?.bronzeAchievedAt) {
        progress.bronzeAchievedAt = existingProgress.bronzeAchievedAt;
      }
      if (existingProgress?.silverAchievedAt) {
        progress.silverAchievedAt = existingProgress.silverAchievedAt;
      }
    }

    // Save to Firestore
    await this.saveUserProgress(userId, progress);
    
    return progress;
  }

  /**
   * Get user's current progress
   */
  public async getUserProgress(userId: string): Promise<UserProgress | null> {
    try {
      const progressDoc = await getDoc(doc(db, 'userProgress', userId));
      if (progressDoc.exists()) {
        const data = progressDoc.data();
        return {
          ...data,
          xmLevelValidUntil: data.xmLevelValidUntil?.toDate() || null,
          lastCalculated: data.lastCalculated?.toDate() || new Date(),
          bronzeAchievedAt: data.bronzeAchievedAt?.toDate(),
          silverAchievedAt: data.silverAchievedAt?.toDate(),
          goldAchievedAt: data.goldAchievedAt?.toDate(),
        } as UserProgress;
      }
      return null;
    } catch (error) {
      console.error('Error getting user progress:', error);
      return null;
    }
  }

  /**
   * Save user progress to Firestore
   */
  private async saveUserProgress(userId: string, progress: UserProgress): Promise<void> {
    try {
      // Ensure all undefined values are converted to null for Firestore
      const firestoreData = {
        ...progress,
        lastCalculated: serverTimestamp(),
        bronzeAchievedAt: progress.bronzeAchievedAt || null,
        silverAchievedAt: progress.silverAchievedAt || null,
        goldAchievedAt: progress.goldAchievedAt || null,
        lastMonthlyReset: progress.lastMonthlyReset || null,
        currentMonth: progress.currentMonth || null,
        monthlyCommission: progress.monthlyCommission ?? null,
        monthlyPoints: progress.monthlyPoints ?? null,
        totalCommission: progress.totalCommission ?? null,
        commissionPoints: progress.commissionPoints ?? null,
        levelMaintenanceStatus: progress.levelMaintenanceStatus || null,
      };

      await setDoc(doc(db, 'userProgress', userId), firestoreData, { merge: true });
    } catch (error) {
      console.error('Error saving user progress:', error);
      throw error;
    }
  }

  /**
   * Get next steps for user to complete
   */
  public getNextSteps(userProfile: any): Array<{
    id: string;
    title: string;
    description: string;
    completed: boolean;
    action: string;
    priority: number;
  }> {
    const steps = [];
    const progress = this.getBronzeProgress(userProfile);

    if (!progress.requirements.tradingExperience.completed) {
      steps.push({
        id: 'trading-experience',
        title: 'Set Your Trading Experience',
        description: 'Tell us about your trading experience level to get personalized content',
        completed: false,
        action: 'Go to Profile',
        priority: 1
      });
    }

    if (!progress.requirements.preferredPairs.completed) {
      steps.push({
        id: 'preferred-pairs',
        title: 'Add Preferred Trading Pairs',
        description: 'Specify your favorite currency pairs for targeted signals and analysis',
        completed: false,
        action: 'Go to Profile',
        priority: 2
      });
    }

    if (!progress.requirements.xmAccountVerified.completed) {
      steps.push({
        id: 'xm-verification',
        title: 'Verify XM Account',
        description: 'Link and verify your XM trading account to unlock Bronze level benefits',
        completed: false,
        action: 'Go to Profile',
        priority: 3
      });
    }

    if (progress.completed) {
      steps.push({
        id: 'bronze-completed',
        title: '🥉 Bronze Level Achieved!',
        description: 'Congratulations! You\'ve unlocked Bronze level benefits',
        completed: true,
        action: 'View Benefits',
        priority: 0
      });
    }

    return steps.sort((a, b) => a.priority - b.priority);
  }

  /**
   * Get level benefits description
   */
  public getLevelBenefits(level: 'bronze' | 'silver' | 'gold'): string[] {
    const benefits = {
      bronze: [
        'Access to basic trading signals',
        'Weekly market analysis',
        'Community chat access',
        'Educational content library'
      ],
      silver: [
        'All Bronze benefits',
        'Premium trading signals',
        'Daily market insights',
        'Priority support'
      ],
      gold: [
        'All Silver benefits',
        'VIP trading signals',
        'Personal account manager',
        'Exclusive webinars'
      ]
    };

    return benefits[level] || [];
  }

  /**
   * Check if XM account number is already in use by another user
   */
  public async checkDuplicateXmAccount(xmAccountNumber: string, currentUserId: string): Promise<{ isDuplicate: boolean; existingUserId?: string }> {
    try {
      if (!xmAccountNumber || xmAccountNumber.trim() === '') {
        return { isDuplicate: false };
      }

      const { collection, query, where, getDocs } = await import('firebase/firestore');
      
      // Query users collection for existing XM account number
      const usersQuery = query(
        collection(db, 'users'),
        where('xmAccountNumber', '==', xmAccountNumber.trim())
      );
      
      const querySnapshot = await getDocs(usersQuery);
      
      // Check if any document exists and it's not the current user
      let isDuplicate = false;
      let existingUserId: string | undefined;
      
      querySnapshot.forEach((doc) => {
        if (doc.id !== currentUserId) {
          isDuplicate = true;
          existingUserId = doc.id;
        }
      });
      
      return { isDuplicate, existingUserId };
    } catch (error) {
      console.error('Error checking duplicate XM account:', error);
      // Return false on error to avoid blocking legitimate saves
      return { isDuplicate: false };
    }
  }

  /**
   * Test function to check commission data for specific XM account
   */
  public async testCommissionData(xmAccountNumber: number): Promise<void> {
    try {
      console.log(`🔍 Testing commission data for XM account: ${xmAccountNumber}`);
      
      const db = getFirestore();
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      
      // Test 1: Query with loginId
      console.log('📊 Testing with loginId field...');
      const loginIdQuery = query(
        collection(db, 'traderStatistics'),
        where('loginId', '==', xmAccountNumber)
      );
      
      const loginIdSnapshot = await getDocs(loginIdQuery);
      console.log(`Found ${loginIdSnapshot.size} documents with loginId = ${xmAccountNumber}`);
      
      let totalCommission = 0;
      let validTrades = 0;
      let commissionsFound: number[] = [];
      
      loginIdSnapshot.forEach((doc) => {
        const data = doc.data();
        const commissionValue = data.commission || 0;
        
        if (commissionValue !== 0) {
          totalCommission += Math.abs(commissionValue);
          validTrades++;
          commissionsFound.push(commissionValue);
        }
        
        // Log first 5 documents for inspection
        if (commissionsFound.length < 5) {
          console.log('Document found:', {
            id: doc.id,
            loginId: data.loginId,
            commission: data.commission,
            commissionType: typeof data.commission,
            openTime: data.openTime,
            closeTime: data.closeTime,
            allFields: Object.keys(data)
          });
        }
      });
      
      console.log(`📊 Commission Summary:`);
      console.log(`- Total documents: ${loginIdSnapshot.size}`);
      console.log(`- Documents with commission: ${validTrades}`);
      console.log(`- Total commission: ${totalCommission}`);
      console.log(`- Commission points: ${Math.floor(totalCommission * 10)}`);
      console.log(`- Sample commissions: [${commissionsFound.slice(0, 10).join(', ')}]`);
      
      // Test 2: Query with userId for comparison
      console.log('📊 Testing with userId field for comparison...');
      const userIdQuery = query(
        collection(db, 'traderStatistics'),
        where('userId', '==', xmAccountNumber)
      );
      
      const userIdSnapshot = await getDocs(userIdQuery);
      console.log(`Found ${userIdSnapshot.size} documents with userId = ${xmAccountNumber}`);
      
      userIdSnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Document found:', {
          id: doc.id,
          userId: data.userId,
          commission: data.commission,
          openTime: data.openTime,
          closeTime: data.closeTime
        });
      });
      
      // Test 3: Get a few sample documents to see the structure
      console.log('📊 Getting sample documents to check structure...');
      const sampleQuery = collection(db, 'traderStatistics');
      const sampleSnapshot = await getDocs(sampleQuery);
      let count = 0;
      sampleSnapshot.forEach((doc) => {
        if (count < 3) {
          const data = doc.data();
          console.log(`Sample document ${count + 1}:`, {
            id: doc.id,
            fields: Object.keys(data),
            loginId: data.loginId,
            userId: data.userId,
            commission: data.commission
          });
          count++;
        }
      });
      
    } catch (error) {
      console.error('❌ Error testing commission data:', error);
    }
  }
}

export default PointsService.getInstance(); 